import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceConfigurationsMap, InvoiceConfigurationsMapCache } from '../../model/model-map-cache';
import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ClientType, Invoice, InvoiceConfiguration, InvoiceState } from 'src/app/api/models';
import { PaginationDataSource } from 'src/app/model/pagination-data-source';
import { InvoicesService } from 'src/app/api/services';
import { financingHoldsTooltip } from 'src/app/shared/financing-holds/financing-hold-text';

class InvoiceQuery {
  q: string;
  states: string[];
  configurations: number[];
  client_types: ClientType[];
}

@Component({
  selector: 'app-invoices-list-page',
  templateUrl: './invoices-list-page.component.html',
  styleUrls: ['./invoices-list-page.component.scss']
})
export class InvoicesListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  @ViewChild('table', { read: ElementRef }) public matTableRef: ElementRef;

  states$: Observable<InvoiceState[]>;
  configurations$: Observable<InvoiceConfiguration[]>;
  configurationsMap$: Observable<InvoiceConfigurationsMap>;

  iq: InvoiceQuery = new InvoiceQuery();
  title: string;

  columns = [
    'financing_on_hold',
    'number',
    'new_number',
    'configuration_id',
    'client.name',
    'end_customer.name',
    'amount.amount',
    'due_date',
    'state.name',
    'created_at',

    /*
    'newInvoiceNumber',
    'configuration',
    'clientRepresentative.client.name',
    'endCustomerContact.endCustomer.name',
    'amount',
    'dueDate',
    'invoiceState.description',
    'created'
    */
  ];

  dataSource = new PaginationDataSource<Invoice, InvoiceQuery>(
    (request, q) => this.invoicesService.listInvoices({
      q: q.q,
      states: q.states,
      configurations: q.configurations,
      client_types: q.client_types,
      after: request.after,
      limit: request.limit
    }),
    { q: '', configurations: [], states: [], client_types: [] },
    50,
  )

  constructor(private invoicesService: InvoicesService,
    private renderer: Renderer2,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private invoiceConfigurationsMapCache: InvoiceConfigurationsMapCache) {
  }

  ngOnInit() {
    this.states$ = this.invoicesService.listInvoiceStates();
    this.configurations$ = this.invoicesService.listInvoiceConfigurations();
    this.configurationsMap$ = this.invoiceConfigurationsMapCache.map$;

    combineLatest([
      this.activatedRoute.queryParamMap,
      this.activatedRoute.data,
    ]).pipe(
      takeUntil(this.ngDestroy$),
    ).subscribe(([params, data]) => {
      this.title = data.title;
      this.queryBy({
        q: params.get('q') || '',
        states: params.getAll('states'),
        configurations: params
          .getAll('configurations')
          .map((v) => Number(v))
          .filter((n) => !Number.isNaN(n)),
        client_types: data.clientType ? [data.clientType] : [],
      });
    });
  }

  public ngAfterViewInit(): void {
    fromEvent<Event>(this.matTableRef.nativeElement, 'scroll')
      .pipe(debounceTime(700))
      .subscribe((e) => this.onTableScroll(e));
  }

  // The hold indicator's tooltip: that the invoice is on financing hold, and
  // each applying hold's scope and reason.
  holdTooltip(invoice: Invoice): string {
    return financingHoldsTooltip(invoice.financing_holds);
  }

  // Update URL query parameters that then triggers changes in datasource
  updateQuery(params: Partial<InvoiceQuery>) {
    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams: params,
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });
  }

  // update Datasource and UI
  queryBy(params: InvoiceQuery) {
    this.iq = params;
    this.dataSource.queryBy(this.iq);
  }

  reset() {
    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams: {},
      });
  }

  private onTableScroll(e: Event): void {
    // The table is the scroll's target: a scroll event does not bubble.
    const table = e.target;
    if (!(table instanceof HTMLElement)) {
      return;
    }
    const tableViewHeight = table.offsetHeight // viewport: ~500px
    const tableScrollHeight = table.scrollHeight // length of all table
    const scrollLocation = table.scrollTop; // how far user scrolled

    // If the user has scrolled within 200px of the bottom, add more data
    const scrollThreshold = 500;

    const scrollUpLimit = scrollThreshold;
    if (scrollLocation < scrollUpLimit) {
      //console.log(`onTableScroll() UP: firstPage decreased to ${this.firstPage}. Now fetching data...`);
      //this.fetchData();
      //this.scrollTo(tableScrollHeight / 2 - 2 * tableViewHeight);
    }

    const scrollDownLimit = tableScrollHeight - tableViewHeight - scrollThreshold;
    if (scrollLocation > scrollDownLimit) {
      //console.log(`onTableScroll(): firstPage increased to ${this.firstPage}. Now fetching data...`);
      //this.fetchData();
      if (this.dataSource.fetchNextPage()) {
        //this.scrollTo(tableScrollHeight / 2 + tableViewHeight);
      }
    }
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
